import type {
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';

const COGNITO_NETWORK_ERROR_FIX_ENABLED = 'cognitoNetworkErrorFixEnabled';

export function setCognitoNetworkErrorFixEnabled(enabled: boolean) {
  window.sessionStorage.setItem(
    COGNITO_NETWORK_ERROR_FIX_ENABLED,
    JSON.stringify(enabled),
  );
}

function getCognitoNetworkErrorFixEnabled() {
  const value = window.sessionStorage.getItem(
    COGNITO_NETWORK_ERROR_FIX_ENABLED,
  );

  if (!value) {
    return false;
  }

  try {
    return JSON.parse(value) === true;
  } catch {
    return false;
  }
}

function waitForOnline() {
  return new Promise<void>(resolve => {
    if (navigator.onLine) {
      resolve();
    } else {
      const handleOnline = () => {
        window.removeEventListener('online', handleOnline);
        resolve();
      };

      window.addEventListener('online', handleOnline);
    }
  });
}

/**
 * Get the Cognito session for the current user.
 * Check for network connectivity before making the request because if the request fails due to network issues, the user will be logged out.
 */
export async function getCognitoSession() {
  // Zendesk build has no Cognito session and Amplify is never configured;
  // calling Auth.currentSession() would issue requests to cognito-idp that
  // 400 because there is no user pool client to talk to. Refuse at the
  // source so any forgotten caller is safely shut down here.
  if (IS_ZENDESK_BUILD) {
    throw new Error('getCognitoSession is unavailable in the Zendesk build');
  }
  if (getCognitoNetworkErrorFixEnabled()) {
    await waitForOnline();
  }
  return Auth.currentSession();
}

/**
 * Force Cognito to mint a new session via the stored refresh token.
 *
 * `Auth.currentSession()` only rotates a token that is already expired, so a
 * near-expiry token would otherwise be reused. Amplify v5 has no `forceRefresh`
 * flag, so drive `CognitoUser.refreshSession()` directly, then re-read.
 */
export async function forceRefreshCognitoSession(): Promise<CognitoUserSession> {
  if (IS_ZENDESK_BUILD) {
    throw new Error(
      'forceRefreshCognitoSession is unavailable in the Zendesk build',
    );
  }
  if (getCognitoNetworkErrorFixEnabled()) {
    await waitForOnline();
  }
  const user: CognitoUser = await Auth.currentAuthenticatedUser();
  const session = user.getSignInUserSession();
  if (!session) {
    throw new Error('No active Cognito session to refresh');
  }
  const refreshToken = session.getRefreshToken();
  await new Promise<void>((resolve, reject) => {
    user.refreshSession(refreshToken, err => (err ? reject(err) : resolve()));
  });
  return Auth.currentSession();
}

/**
 * Get the current Cognito ID token as a JWT string.
 *
 * With no options: a passive read (unchanged behavior). When `ensureFreshForMs`
 * is set and the token expires within that window, it is force-rotated first; a
 * failed rotation falls back to the current token instead of throwing.
 */
export async function getCognitoToken(opts?: {
  ensureFreshForMs?: number;
}): Promise<string> {
  const session = await getCognitoSession();
  const ensureFreshForMs = opts?.ensureFreshForMs;
  if (ensureFreshForMs != null) {
    const expiresAtMs = session.getIdToken().getExpiration() * 1000;
    if (expiresAtMs - Date.now() < ensureFreshForMs) {
      try {
        const freshSession = await forceRefreshCognitoSession();
        return freshSession.getIdToken().getJwtToken();
      } catch (err) {
        // Rotation failed — fall back to the (soon-to-expire) current token.
        console.warn('Failed to force-refresh Cognito token:', err);
      }
    }
  }
  return session.getIdToken().getJwtToken();
}
